'use client';

import { useQuantityInput } from './use-quantity-input';

import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma "Quantity": 52 px pill, 1 px border/subtle, 16 px side padding, minus/plus 20 px, value Bold 16.
 * Presentational – the caller owns the value (cart store, product form).
 */
export function QuantityStepper({
  value,
  onCommit,
  step = 1,
  min = 1,
  max,
  disabled,
  label,
  onRemove,
  removeLabel = 'Odstrániť',
  className,
}: {
  value: number;
  onCommit: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  /** Accessible name, e.g. „Množstvo“. */
  label: string;
  /** At the minimum the minus stays enabled and removes the line instead (cart). */
  onRemove?: () => void;
  removeLabel?: string;
  className?: string;
}) {
  const input = useQuantityInput({ value, min, max, disabled, onCommit });
  const removes = !!onRemove && input.current <= min;
  const button =
    'flex size-7 shrink-0 items-center justify-center rounded-full text-[#0F1012] transition-colors hover:bg-[#F5F6F7] disabled:text-[#B9BDC3] disabled:hover:bg-transparent';

  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'flex h-[52px] w-[148px] shrink-0 items-center justify-between rounded-full border border-[#E2E4E7] bg-white px-3',
        className,
      )}
    >
      <button
        type="button"
        className={button}
        onPointerDown={event => event.preventDefault()}
        onClick={() => {
          if (removes) {
            input.reset();
            onRemove?.();
          } else input.stepBy(-step);
        }}
        disabled={disabled || (input.current <= min && !removes)}
        aria-label={removes ? removeLabel : 'Menej'}
      >
        <Icon name="minus-20" />
      </button>
      <input
        {...input.inputProps}
        aria-label={label}
        className="min-w-0 w-0 flex-1 rounded bg-transparent text-center text-base font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0F1012]"
      />
      <button
        type="button"
        className={button}
        onPointerDown={event => event.preventDefault()}
        onClick={() => input.stepBy(step)}
        disabled={disabled || (max !== undefined && input.current >= max)}
        aria-label="Viac"
      >
        <Icon name="plus-20" />
      </button>
    </div>
  );
}
